/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, onConnect, reaction } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'
import { match } from 'ts-pattern'

import { AsyncStateN, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import {
  fetchUserPositionsRequestAction,
  positionsRegistry,
} from '@root/entities/position'
import { userPositionUpdateEvent } from '@root/entities/realtime'
import { deviceWalletIdsAtom } from '@root/features/secure-storage/atoms'
import { STORAGE_PREFIX } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'

import type { tradeApi } from '@blade/socket-types'
import type { UserPositionsCreatePayload } from '@blade/trade-api-client'
import type { Network } from '@root/entities/network'
import type { WalletId } from '@root/entities/wallet'

export type TopbarPositionsMode = 'recent' | 'amount'

export const topbarPositionsModeAtom = atom<TopbarPositionsMode>(
  'recent',
  'topbarPositionsModeAtom',
).pipe(withLocalStorage(`${STORAGE_PREFIX}/topbarPositionsMode`))

const TOPBAR_ITEMS_COUNT = 20

export const reatomTopbarPositions = () => {
  const loadPositionsAction = action(
    async (
      ctx,
      args: {
        mode: TopbarPositionsMode
        network: Network.Network
        walletIds: Array<WalletId>
      },
    ) => {
      const sortBy = match(args.mode)
        .returnType<UserPositionsCreatePayload['sortBy']>()
        .with('amount', () => 'remainingSizeUsd')
        .with('recent', () => 'lastTrade')
        .exhaustive()

      const result = await ctx.schedule(() =>
        fetchUserPositionsRequestAction(ctx, {
          network: args.network,
          sortBy,
          sortOrder: 'desc',
          hidden: false,
          limit: TOPBAR_ITEMS_COUNT,
          walletIds: [...args.walletIds],
        }),
      )

      return result.pipe(
        Result.map((it) => ({
          ...it,
          data: it.data.map((it) => positionsRegistry.resolveFromDto(ctx, it)),
        })),
      )
    },
    'loadPositionsAction',
  )

  const positionsStateAtom = reatomRequest({
    execAction: loadPositionsAction,
  })

  const loadReaction = reaction((ctx) => {
    const network = ctx.spy(selectedNetworkAtom)
    const mode = ctx.spy(topbarPositionsModeAtom)
    const walletIds = ctx.spy(deviceWalletIdsAtom)
    if (walletIds.size === 0) {
      positionsStateAtom.setState(
        ctx,
        AsyncStateN.success({ data: [], meta: { total: 0 } }),
      )
      return
    }

    positionsStateAtom.execute(ctx, {
      network,
      mode,
      walletIds: [...walletIds],
    })
  }, 'loadReaction')

  const handlePositionUpdate = action(
    (ctx, update: tradeApi.UserPositionData) => {
      const state = ctx.get(positionsStateAtom)
      if (AsyncStateN.isLoading(state)) {
        return
      }
      if (AsyncStateN.isError(state) || AsyncStateN.isNotInitiated(state)) {
        positionsStateAtom.refresh(ctx, { strategy: 'full' })
        return
      }

      const positions = state.value.data

      const alreadyExists = positions.some(
        (it) => it.pairAddress === update.pair,
      )

      if (!alreadyExists) {
        positionsStateAtom.refresh(ctx, { strategy: 'success-only' })
      }
    },
    'handlePositionUpdate',
  )

  onConnect(positionsStateAtom, (ctx) => {
    const sub = loadReaction(ctx)
    const unsubscribeEvent = userPositionUpdateEvent.onCall(
      (ctx, walletPosition) => {
        handlePositionUpdate(ctx, walletPosition)
      },
    )

    return () => {
      sub.unsubscribe()
      unsubscribeEvent()
    }
  })

  const visiblePositionsStateAtom = atom((ctx) => {
    const state = ctx.spy(positionsStateAtom)
    return AsyncStateN.map(state, (state) => {
      const visiblePositions = state.data.filter((it) => {
        const hidden = ctx.spy(it.hidden)
        return hidden === false
      })

      return {
        ...state,
        data: visiblePositions,
      }
    })
  })

  return {
    positionsStateAtom,
    visiblePositionsStateAtom,
  }
}
